import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { MENU_CONFIG, type AppUser } from '../../data/menu';
import { UserRole } from '../../types/UserRole';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface SidebarProps {
  currentUser: AppUser;
}

export const Sidebar = ({ currentUser }: SidebarProps) => {
  const location = useLocation();
  // const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});

  const isAdmin = currentUser.role === UserRole.Admin;

  // --- 権限チェック関数 ---
  // 役割（role）に加えて、権限（permission）もチェックする
  const canAccessMenu = (item: typeof MENU_CONFIG[0]['items'][0]) => {
    // 1. 役割のチェック
    const hasRole = item.roles.includes(currentUser.role) || item.roles.includes('all');
    if (!hasRole) return false;

    // 2. 権限のチェック（設定されているメニュー項目のみ評価）
    if (item.permissions && item.permissions.length > 0) {
      if (!currentUser.permission) return false; // ユーザーに権限データが無ければ非表示
      
      // 文字列と数値の不一致を防ぐため Number() で安全に比較
      const userPermNum = Number(currentUser.permission);
      return item.permissions.some(p => Number(p) === userPermNum);
    }

    return true;
  };

  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(() => {
    if (isAdmin) {
      return {}; // 管理者は閉じてスタート
    } else {
      // 管理者以外は、すべてのカテゴリ名をキーにして true をセット
      const allOpen: Record<string, boolean> = {};
      MENU_CONFIG.forEach(cat => {
        allOpen[cat.category] = true;
      });
      return allOpen;
    }
  });

  // // URLが変わるたびに「現在地が含まれるカテゴリ」を特定して開く
  // useEffect(() => {
  //   // 1. 現在のURLにマッチするカテゴリを探す
  //   const activeCategory = MENU_CONFIG.find(category => 
  //     category.items.some(item => 
  //       // パスが完全一致、または前方一致（/admin/users など）しているか
  //       location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  //     )
  //   );
    

  //   // 2. マッチするカテゴリがあれば、そのカテゴリを「開く(true)」に設定
  //   if (activeCategory) {
  //     setOpenCategories(prev => ({
  //       ...prev,
  //       [activeCategory.category]: true
  //     }));
  //   }
  // }, [location.pathname]); // URLが変わるたびに実行

  // // 手動で開閉ボタンを押したときの処理
  // const toggleCategory = (categoryName: string) => {
  //   setOpenCategories(prev => ({
  //     ...prev,
  //     [categoryName]: !prev[categoryName]
  //   }));
  // };
  useEffect(() => {
    if (!isAdmin) return;

    const activeCategory = MENU_CONFIG.find(category => 
      category.items.some(item => 
        location.pathname === item.path || location.pathname.startsWith(item.path + '/')
      )
    );

    if (activeCategory) {
      setOpenCategories(prev => ({
        ...prev,
        [activeCategory.category]: true
      }));
    }
  }, [location.pathname, isAdmin]);

  // 手動開閉 (全ロールで使用可能)
  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  return (
    <nav className="px-3 space-y-4">
      {MENU_CONFIG.map((category, index) => {
        // 利用可能なメニュー項目を、改修した canAccessMenu で絞り込む
        const availableItems = category.items.filter(item => canAccessMenu(item));
        
        if (availableItems.length === 0) return null;
        if (category.category === 'ホーム') return null;

        const Icon = category.icon;
        
        // そのカテゴリが開いているかどうか (trueなら開く)
        const isOpen = openCategories[category.category] === true;

        return (
          <div key={index}>
            {/* カテゴリヘッダー */}
            <button
              onClick={() => toggleCategory(category.category)}
              className={`
                w-full flex items-center justify-between px-3 py-3 
                rounded-lg transition-all duration-200 group
                bg-slate-700/40 hover:bg-slate-700
                text-slate-200 hover:text-white
                ${isOpen ? 'ring-1 ring-slate-600/50' : ''}
              `}
            >
              <div className="flex items-center gap-3">
                <Icon size={18} className="text-slate-400 group-hover:text-white transition-colors" />
                <span className="text-sm font-bold tracking-wide">{category.category}</span>
              </div>

              {isOpen ? (
                <ChevronDown size={16} className="text-slate-400 group-hover:text-white transition-colors" />
              ) : (
                <ChevronRight size={16} className="text-slate-400 group-hover:text-white transition-colors" />
              )}
            </button>
            
            {/* メニューリスト */}
            {isOpen && (
              <div className="space-y-1 mt-2 ml-1 border-l-2 border-slate-700/50 pl-2 transition-all duration-200 ease-in-out">
                {availableItems.map((item, itemIndex) => (
                  <NavLink
                    key={itemIndex}
                    to={item.path}
                    onClick={(e) => {
                      //現在のパスとクリックしたパスが同じなら、遷移をキャンセルして何もしない
                      if (location.pathname === item.path) {
                        e.preventDefault(); 
                      }
                    }}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all
                      ${isActive 
                        ? 'bg-blue-600 text-white shadow-md' 
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white hover:pl-4'
                      }
                    `}
                  >
                    <span>{item.name}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};